import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastController } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { llmServiceFetcher } from '../LlmServiceFetcher';

interface BulkEvalAgentThreadsResponse {
    accepted: number;
}

interface BulkEvalAgentThreadsInput {
    agentId: string;
}

export const useBulkEvalAgentThreads = () => {
    const queryClient = useQueryClient();
    const toast = useToastController();

    return useMutation({
        mutationFn: async ({
            agentId,
        }: BulkEvalAgentThreadsInput): Promise<BulkEvalAgentThreadsResponse> => {
            return await llmServiceFetcher<BulkEvalAgentThreadsResponse>({
                endpoint: `/agents/${encodeURIComponent(agentId)}/threads/eval/bulk`,
                method: 'POST',
            });
        },
        onSuccess: async (result, { agentId }) => {
            const invalidateThreads = async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['mastraMemoryThreads', agentId],
                    refetchType: 'all',
                });
            };

            await invalidateThreads();

            // Soft-poll while background evals complete and write metadata.
            if (result.accepted > 0) {
                for (const delayMs of [15_000, 45_000, 90_000, 180_000]) {
                    setTimeout(() => {
                        void invalidateThreads();
                    }, delayMs);
                }
            }

            if (result.accepted === 0) {
                toast.show(
                    'No unevaluated threads found.',
                    MutationSuccessToastOptions,
                );
                return;
            }

            toast.show(
                `Queued eval for ${result.accepted} thread${result.accepted === 1 ? '' : 's'}.`,
                MutationSuccessToastOptions,
            );
        },
        onError: (error) => {
            toast.show(
                'Failed to queue bulk eval.',
                MutationErrorToastOptions(error.message),
            );
        },
    });
};
